import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiPlus, FiSend, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { FunilMensagensCard, MensagensEtapaFiltros, MensagensEtapaPaginacao } from '../components/mensagensEtapa';
import { mensagensEtapaAPI, funisProspeccaoAPI, etapasAPI, origensLeadAPI } from '../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const EmptyAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.error[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const MensagensEtapa = () => {
  const navigate = useNavigate();
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [funis, setFunis] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [origens, setOrigens] = useState([]);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filtros, setFiltros] = useState({
    search: '',
    funil_id: '',
    etapa_id: '',
    canal: '',
    ativa: '',
    page: 1,
    limit: 10
  });

  const carregarFunis = async () => {
    try {
      const response = await funisProspeccaoAPI.listarTodos();
      if (response.data.success) {
        setFunis(response.data.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar funis:', err);
    }
  };

  const carregarEtapasDoFunil = async (funilId) => {
    if (!funilId) {
      setEtapas([]);
      return;
    }

    try {
      setLoadingEtapas(true);
      const response = await etapasAPI.listarPorFunil(funilId);
      if (response.data.success) {
        setEtapas(response.data.data || response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar etapas:', err);
      setEtapas([]);
    } finally {
      setLoadingEtapas(false);
    }
  };

  const carregarOrigens = async () => {
    try {
      const response = await origensLeadAPI.listar();
      if (response.data.success) {
        setOrigens(response.data.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar origens de lead:', err);
    }
  };

  const carregarMensagens = async (params) => {
    try {
      setLoading(true);
      setError(null);

      if (import.meta.env.DEV) {
        console.debug('[MensagensEtapa] Carregando mensagens', params);
      }

      const response = await mensagensEtapaAPI.listar(params);

      if (response.data.success) {
        const mensagensApi = response.data.data || [];
        const paginationApi = response.data.pagination || {
          currentPage: params?.page || filtros.page || 1,
          totalPages: 1,
          totalItems: mensagensApi.length,
          itemsPerPage: params?.limit || filtros.limit || 10,
          hasNextPage: false,
          hasPrevPage: false
        };
        setMensagens(mensagensApi);
        setPagination(paginationApi);
      } else {
        setError('Erro ao carregar mensagens');
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarFunis();
    carregarOrigens();
    carregarMensagens(filtros);
  }, []);

  const handleFiltrosChange = (novosFiltros) => {
    setFiltros({
      ...novosFiltros,
      page: 1,
      limit: novosFiltros.limit || filtros.limit || 10
    });
  };

  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarMensagens({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarMensagens(filtrosParaBusca);
  };

  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarMensagens(novosFiltros);
  };

  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = {
      ...filtros,
      limit: newLimit,
      page: 1
    };
    setFiltros(novosFiltros);
    carregarMensagens(novosFiltros);
  };

  const handleAdicionarMensagem = () => {
    navigate('/leads/mensagens-etapa/novo');
  };

  const handleFunilChange = (funilId) => {
    carregarEtapasDoFunil(funilId);
  };

  const agruparMensagensPorFunil = (mensagens) => {
    const grupos = {};
    
    mensagens.forEach((mensagem) => {
      const funilId = mensagem.funil_id || 'sem-funil';
      const funilNome = mensagem.funil_nome || 'Sem Funil';
      
      if (!grupos[funilId]) {
        grupos[funilId] = {
          funil_id: funilId,
          funil_nome: funilNome,
          mensagens: []
        };
      }
      
      grupos[funilId].mensagens.push(mensagem);
    });
    
    // Ordenar grupos por nome do funil e mensagens por ordem da etapa
    return Object.values(grupos).sort((a, b) => {
      const nomeA = a.funil_nome.toLowerCase();
      const nomeB = b.funil_nome.toLowerCase();
      return nomeA.localeCompare(nomeB);
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando mensagens...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Erro ao carregar mensagens</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarMensagens(filtros)}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (mensagens.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiSend />
          </EmptyIcon>
          <EmptyTitle>Nenhuma mensagem configurada</EmptyTitle>
          <EmptyText>
            Não encontramos mensagens para as etapas com os filtros aplicados.
          </EmptyText>
          <EmptyAction onClick={handleAdicionarMensagem}>
            <FiPlus />
            Cadastrar Primeira Mensagem
          </EmptyAction>
        </EmptyState>
      );
    }

    const gruposFunil = agruparMensagensPorFunil(mensagens);

    return (
      <>
        <CardsGrid>
          {gruposFunil.map((grupo) => (
            <FunilMensagensCard
              key={grupo.funil_id}
              funil={grupo}
            />
          ))}
        </CardsGrid>

        {pagination.totalPages > 1 && (
          <MensagensEtapaPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Mensagens por Etapa">
      <Container>
        <Header>
          <AddButton onClick={handleAdicionarMensagem}>
            <FiPlus />
            Nova Mensagem
          </AddButton>
        </Header>

        <Content>
          <MensagensEtapaFiltros
            filtros={filtros}
            funis={funis}
            etapas={etapas}
            loadingEtapas={loadingEtapas}
            origens={origens}
            onFunilChange={handleFunilChange}
            onFiltrosChange={handleFiltrosChange}
            onBuscar={handleBuscar}
            loading={loading}
          />

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default MensagensEtapa;

